import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { AutomationRoutes } from 'modules/Settings/Automation';
import { InvoiceSettings } from 'modules/Settings/Invoicing/InvoiceSettings';
import { TemplateRoutes } from 'modules/Settings/Templates';
import { WorkflowsRoutes } from 'modules/Settings/Workflows';
import { Redirect, Route, type RouteComponentProps, Switch } from 'react-router-dom';
import {
  Chift,
  Codabox,
  CompanyWeb,
  Digiteal,
  Exact,
  Horus,
  INTEGRATION_ROUTE,
  INTEGRATIONS_ROUTE,
  Integration,
  isEnabledIntegration,
  Ponto,
  Teamleader,
  Yuki,
} from 'shared/components/Integrations';
import { DBasics } from 'shared/components/Integrations/components/DBasics/DBasics';
import { Variables } from '~/modules/Settings/Variables/Variables';
import { Collaborators } from './Collaborators/Collaborators';
import { CompanySettings } from './Company/Company';
import { ConfigurationSettings } from './Configuration/Configuration';
import { IntegrationsSettings } from './Integrations/Integrations';
import { Profile } from './Profile/Profile';
import styleIdentifiers from './Settings.module.scss';

const styles = classNames.bind(styleIdentifiers);

export default function Settings() {
  const company = useCompanyConfiguration();
  const profile = useProfile();

  const canEditWorkflows = profile.isAdmin || profile.permissions.canManageRecoveryPlans;
  const canUseAutomation = company.package.automation !== 'no_automations';

  return (
    <div className={styles('Settings')}>
      <Switch>
        <Route path="/settings/user" component={Profile} />
        {profile.isAdmin && <Route path="/settings/company" component={CompanySettings} />}
        {profile.isAdmin && (
          <Route path="/settings/configuration" component={ConfigurationSettings} />
        )}
        {/* The configuration page used to live at /settings/preferences: keep bookmarks and
            in-flight Microsoft OAuth callbacks (which carry a ?message) working. */}
        <Route
          path="/settings/preferences"
          render={({ location }: RouteComponentProps) => (
            <Redirect to={{ ...location, pathname: '/settings/configuration' }} />
          )}
        />
        {profile.isAdmin && <Route path="/settings/billing" component={InvoiceSettings} />}
        {/* The editor and index used to live under /settings/document-customization:
            keep bookmarks working. */}
        <Redirect
          exact
          from="/settings/document-customization/edit/:id"
          to="/settings/templates/:id"
        />
        <Route
          path="/settings/document-customization"
          render={() => <Redirect to="/settings/templates" />}
        />
        {canEditWorkflows && <Route path="/settings/workflows" component={WorkflowsRoutes} />}
        {profile.isAdmin && <Route path="/settings/collaborators" component={Collaborators} />}
        {profile.isAdmin && (
          <Route path={INTEGRATIONS_ROUTE} exact component={IntegrationsSettings} />
        )}
        {profile.isAdmin && (
          <Route
            exact
            path="/settings/variables"
            render={() => <Redirect to="/settings/variables/custom-variables" />}
          />
        )}
        <Route path="/settings/variables" component={Variables} />
        {canUseAutomation && <Route path="/settings/automations" component={AutomationRoutes} />}
        <Route path="/settings/templates" component={TemplateRoutes} />
        <Route
          path="/settings/integrations"
          render={() => (
            <>
              {isEnabledIntegration(Integration.YUKI) && (
                <Route path={INTEGRATION_ROUTE[Integration.YUKI]} component={Yuki} />
              )}
              {isEnabledIntegration(Integration.HORUS) && (
                <Route path={INTEGRATION_ROUTE[Integration.HORUS]} component={Horus} />
              )}
              {isEnabledIntegration(Integration.TEAMLEADER) && (
                <Route path={INTEGRATION_ROUTE[Integration.TEAMLEADER]} component={Teamleader} />
              )}
              {isEnabledIntegration(Integration.EXACT) && (
                <Route path={INTEGRATION_ROUTE[Integration.EXACT]} component={Exact} />
              )}
              {isEnabledIntegration(Integration.DBASICS) && (
                <Route path={INTEGRATION_ROUTE[Integration.DBASICS]} component={DBasics} />
              )}
              {isEnabledIntegration(Integration.CODABOX) && (
                <Route path={INTEGRATION_ROUTE[Integration.CODABOX]} component={Codabox} />
              )}
              {isEnabledIntegration(Integration.PONTO) && (
                <Route path={INTEGRATION_ROUTE[Integration.PONTO]} component={Ponto} />
              )}
              {isEnabledIntegration(Integration.DIGITEAL) && (
                <Route path={INTEGRATION_ROUTE[Integration.DIGITEAL]} component={Digiteal} />
              )}
              {isEnabledIntegration(Integration.COMPANYWEB) && (
                <Route path={INTEGRATION_ROUTE[Integration.COMPANYWEB]} component={CompanyWeb} />
              )}
              {isEnabledIntegration(Integration.CHIFT) && (
                <Route path={INTEGRATION_ROUTE[Integration.CHIFT]} component={Chift} />
              )}
            </>
          )}
        />
      </Switch>
    </div>
  );
}
